import {
  type AnimationAction,
  type AnimationClip,
  AnimationMixer,
  Box3,
  LoopOnce,
  LoopRepeat,
  type Object3D,
  PropertyBinding,
  type Group,
} from "three/webgpu";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js";

export interface PlayOptions {
  /** Seconds to cross-fade from whatever was playing. 0.25 by default. */
  fade?: number;
  /** Playback rate. */
  speed?: number;
}

/**
 * A glTF model loaded once, from which any number of independent copies are made - each with its own
 * skeleton, pose and animation, sharing geometry and textures.
 *
 * Built on three's `GLTFLoader`, so everything it reads (KHR materials, Draco and meshopt with the
 * decoders configured on `Model.loader`, morph targets, cameras) comes with it.
 */
export class Model {
  /** The loader every `Model.load` goes through; configure Draco or KTX2 decoders on it once. */
  static readonly loader = new GLTFLoader();

  readonly scene: Group;
  readonly animations: AnimationClip[];
  /** The model's bounds in its own units, in its bind pose. */
  readonly bounds: Box3;

  private constructor(scene: Group, animations: AnimationClip[]) {
    this.scene = scene;
    this.animations = animations;
    scene.updateMatrixWorld(true);
    this.bounds = new Box3().setFromObject(scene, true);
  }

  static async load(url: string): Promise<Model> {
    const gltf = await Model.loader.loadAsync(url);
    return new Model(gltf.scene, gltf.animations);
  }

  get clipNames(): string[] {
    return this.animations.map((c) => c.name);
  }

  /**
   * A clip by name: exactly, then ignoring case, then by the part after the last `|` - exporters prefix
   * clips with their armature (`Armature|Idle`), and nobody asking for "idle" means the prefix.
   */
  findClip(name: string): AnimationClip | null {
    const lower = name.toLowerCase();
    const tail = (n: string): string => n.slice(n.lastIndexOf("|") + 1).toLowerCase();
    return (
      this.animations.find((c) => c.name === name) ??
      this.animations.find((c) => c.name.toLowerCase() === lower) ??
      this.animations.find((c) => tail(c.name) === lower) ??
      null
    );
  }

  /** A copy to put in a scene: its own skeleton and mixer, the model's geometry and textures. */
  instantiate(): ModelInstance {
    return new ModelInstance(this, cloneSkinned(this.scene) as Group);
  }

  /** Free the GPU resources every copy shares. */
  dispose(): void {
    this.scene.traverse((o) => {
      const mesh = o as Object3D & { geometry?: { dispose(): void }; material?: unknown };
      mesh.geometry?.dispose();
      for (const m of [mesh.material].flat()) (m as { dispose?: () => void } | undefined)?.dispose?.();
    });
  }
}

/** One copy of a `Model` in a scene. */
export class ModelInstance {
  readonly model: Model;
  readonly root: Group;
  readonly mixer: AnimationMixer;
  #current: AnimationAction | null = null;

  constructor(model: Model, root: Group) {
    this.model = model;
    this.root = root;
    this.mixer = new AnimationMixer(root);
  }

  /** Loop a clip, cross-fading from the last. False when the model has no clip by that name. */
  play(name: string, options: PlayOptions = {}): boolean {
    return this.#start(name, options, false);
  }

  /** Play a clip once and hold its last pose. */
  playOnce(name: string, options: PlayOptions = {}): boolean {
    return this.#start(name, options, true);
  }

  #start(name: string, options: PlayOptions, once: boolean): boolean {
    const clip = this.model.findClip(name);
    if (!clip) return false;
    const action = this.mixer.clipAction(clip);
    action.reset();
    action.setLoop(once ? LoopOnce : LoopRepeat, Infinity);
    action.clampWhenFinished = once;
    action.timeScale = options.speed ?? 1;
    const fade = options.fade ?? 0.25;
    if (this.#current && this.#current !== action && fade > 0) action.crossFadeFrom(this.#current, fade, false);
    action.play();
    this.#current = action;
    return true;
  }

  /**
   * A node by the name the model gave it. three's loader strips the characters animation paths reserve
   * (`Arm.R` arrives as `ArmR`), so either spelling finds it.
   */
  findNode(name: string): Object3D | null {
    let found = this.root.getObjectByName(name) ?? this.root.getObjectByName(PropertyBinding.sanitizeNodeName(name)) ?? null;
    if (!found) this.root.traverse((o) => (found ??= (o.userData as { name?: unknown }).name === name ? o : null));
    return found;
  }

  update(dt: number): void {
    this.mixer.update(dt);
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.root.removeFromParent();
  }
}
